import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LetterTile from '../features/scrabble/LetterTile';
import { LETTER_POINTS } from '../features/scrabble/config/gameConfig';
import {
  GAME_STATE_CHANGED_EVENT,
  getStoredGameState,
} from '../features/scrabble/services/localStorageService';

const readRunState = () => {
  const stored = getStoredGameState();
  if (!stored || typeof stored !== 'object') {
    return null;
  }

  return stored;
};

function SnapshotRow({ label, value, emphasize = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: emphasize ? 800 : 700 }}>{value}</Typography>
    </Box>
  );
}

function RunSnapshotWidget() {
  const [runState, setRunState] = React.useState(() => readRunState());

  React.useEffect(() => {
    const syncRunState = () => {
      setRunState(readRunState());
    };

    syncRunState();

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener(GAME_STATE_CHANGED_EVENT, syncRunState);
    window.addEventListener('storage', syncRunState);

    return () => {
      window.removeEventListener(GAME_STATE_CHANGED_EVENT, syncRunState);
      window.removeEventListener('storage', syncRunState);
    };
  }, []);

  if (!runState) {
    return null;
  }

  const score = Math.max(0, Number(runState.score || 0));
  const roundIndex = Math.max(1, Number(runState.roundIndex || 1));
  const roundTarget = Math.max(1, Number(runState.roundTarget || 1));
  const submitsRemaining = Math.max(0, Number(runState.submitsRemaining || 0));
  const discardsRemaining = Math.max(0, Number(runState.discardsRemaining || 0));
  const pointsToTarget = Math.max(0, roundTarget - score);
  const rackTiles = Array.isArray(runState.rackTiles) ? runState.rackTiles.filter(Boolean) : [];

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.2,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>
          Run Snapshot
        </Typography>
        <Chip
          size="small"
          label={`Round ${roundIndex}`}
          sx={{ height: 20, fontSize: 10.5, fontWeight: 800 }}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 0.7 }}>
        <SnapshotRow label="Score" value={`${score} / ${roundTarget}`} emphasize />
        <SnapshotRow label="Submits Left" value={submitsRemaining} emphasize={submitsRemaining <= 1} />
        <SnapshotRow label="Discards Left" value={discardsRemaining} emphasize={discardsRemaining <= 1} />
      </Stack>

      <Divider sx={{ my: 0.9 }} />

      <Stack spacing={0.65}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>
          Current Rack
        </Typography>

        {!rackTiles.length ? (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            Rack unavailable.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.45,
            }}
          >
            {rackTiles.slice(0, 7).map((tile) => (
              <Box key={tile.id} sx={{ minWidth: 0 }}>
                <LetterTile
                  tile={{
                    ...tile,
                    points: LETTER_POINTS[String(tile?.letter || '').toUpperCase()] || 0,
                  }}
                  draggable={false}
                  compact
                  variant="rack"
                />
              </Box>
            ))}
          </Box>
        )}
      </Stack>

      <Divider sx={{ my: 0.9 }} />

      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
        {pointsToTarget > 0
          ? `${pointsToTarget} points to reach the round target.`
          : 'Target reached. Continue the run to enter the shop.'}
      </Typography>
    </Box>
  );
}

export default RunSnapshotWidget;
